import React from 'react';
import PropTypes from 'prop-types';
import { LuBoxes, LuClipboardList, LuLayers, LuPackage, LuUser, LuUserX, LuUsers } from 'react-icons/lu';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import FilterTiles from '../../../components/data/FilterTiles';
import { EmptyState, LoadingRows, SearchField } from '../../ProfilePage/components/ProfileUi';
import useStockText from '../stockText';
import { EmployeeCard, StockCard } from './assignedCards';

const withCount = (text, count) => (
  <span className="tw-inline-flex tw-items-center tw-gap-1.5">
    {text}
    <span className="tw-rounded-full tw-bg-slate-900/[0.07] tw-px-1.5 tw-text-[10px] tw-font-bold tw-tabular-nums dark:tw-bg-white/10">{count}</span>
  </span>
);

/**
 * "Atanan Stoklar": who holds which stock. Totals, a search, by-employee view
 * (employees without any assignment can be picked out) and by-stock view.
 */
const AssignedStocksModal = ({ isOpen, onClose, loading, data, search, setSearch, viewMode, setViewMode, employeeFilter, setEmployeeFilter }) => {
  const { t, language, label, formatNumber } = useStockText();

  const totalAssignments = data.byEmployee.reduce((sum, e) => sum + e.assignments.length, 0);
  const totalQty = data.byEmployee.reduce((sum, e) => sum + e.assignments.reduce((s, a) => s + (parseFloat(a.quantity) || 0), 0), 0);
  // "Çalışan" yalnızca ataması olanları sayar; atama yapılmamışlar ayrı kutuda
  const withAssignment = data.byEmployee.filter((e) => e.assignments.length > 0).length;
  const withoutAssignment = data.byEmployee.length - withAssignment;
  const term = search.toLocaleLowerCase('tr');

  const employees = data.byEmployee.filter((emp) => {
    const hasAssignment = emp.assignments.length > 0;
    if (employeeFilter === 'assigned' && !hasAssignment) return false;
    if (employeeFilter === 'unassigned' && hasAssignment) return false;
    if (!term) return true;
    return emp.employeeName.toLocaleLowerCase('tr').includes(term)
      || emp.assignments.some((a) => (a.stockName || '').toLocaleLowerCase('tr').includes(term));
  });
  const stocks = data.byStock.filter((s) => {
    if (!term) return true;
    return s.stockName.toLocaleLowerCase('tr').includes(term)
      || s.assignments.some((a) => (a.employeeName || '').toLocaleLowerCase('tr').includes(term));
  });

  const tiles = [
    { key: 'total', label: label('assigned.summary.total'), value: formatNumber(totalAssignments), icon: LuClipboardList, tone: 'info', static: true },
    { key: 'employees', label: label('assigned.summary.employees'), value: formatNumber(withAssignment), icon: LuUsers, tone: 'violet', static: true },
    { key: 'none', label: label('assigned.summary.none'), value: formatNumber(withoutAssignment), icon: LuUserX, tone: 'neutral', static: true },
    { key: 'products', label: label('assigned.summary.products'), value: formatNumber(data.byStock.length), icon: LuPackage, tone: 'success', static: true },
    { key: 'quantity', label: label('assigned.summary.quantity'), value: formatNumber(totalQty), icon: LuLayers, tone: 'warning', static: true, className: 'tw-col-span-2 sm:tw-col-span-1' },
  ];

  let emptyText = label('assigned.empty.noneYet');
  if (search) emptyText = label('assigned.empty.search');
  else if (employeeFilter === 'unassigned') emptyText = label('assigned.empty.allAssigned');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={label('assigned.title')}
      subtitle={label('assigned.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30" size="xs">
          <LuUsers aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {loading ? (
        <LoadingRows count={6} height="tw-h-16" label={label('assigned.loading')} />
      ) : (
        <div className="tw-space-y-4">
          <FilterTiles tiles={tiles} label={label('assigned.summary.label')} className="tw-grid-cols-2 sm:tw-grid-cols-5" />

          <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder={viewMode === 'employee' ? label('assigned.searchEmployee') : label('assigned.searchStock')}
              className="md:tw-flex-1"
            />
            <SegmentedControl
              options={[
                { value: 'employee', label: <span className="tw-inline-flex tw-items-center tw-gap-1.5"><LuUser aria-hidden="true" className="tw-h-4 tw-w-4" />{withCount(label('assigned.views.employee'), data.byEmployee.length)}</span> },
                { value: 'stock', label: <span className="tw-inline-flex tw-items-center tw-gap-1.5"><LuBoxes aria-hidden="true" className="tw-h-4 tw-w-4" />{withCount(label('assigned.views.stock'), data.byStock.length)}</span> },
              ]}
              value={viewMode}
              onChange={setViewMode}
              layoutId="assigned-view"
              label={label('assigned.views.label')}
            />
          </div>

          {viewMode === 'employee' && (
            <>
              <div className="tw-max-w-full tw-overflow-x-auto">
                <SegmentedControl
                  size="sm"
                  options={[
                    { value: 'all', label: withCount(label('assigned.filters.all'), data.byEmployee.length) },
                    { value: 'assigned', label: withCount(label('assigned.filters.assigned'), withAssignment) },
                    { value: 'unassigned', label: withCount(label('assigned.filters.unassigned'), withoutAssignment) },
                  ]}
                  value={employeeFilter}
                  onChange={setEmployeeFilter}
                  layoutId="assigned-employee-filter"
                  label={label('assigned.filters.label')}
                />
              </div>
              {employees.length === 0 ? (
                <EmptyState icon={LuUser} title={label('assigned.empty.title')} text={emptyText} compact />
              ) : (
                <div className="tw-grid tw-gap-3 md:tw-grid-cols-2 xl:tw-grid-cols-3">
                  {employees.map((emp) => <EmployeeCard key={emp.employeeId} employee={emp} search={search} />)}
                </div>
              )}
            </>
          )}

          {viewMode === 'stock' && (
            stocks.length === 0 ? (
              <EmptyState icon={LuPackage} title={label('assigned.empty.stockTitle')} text={search ? label('assigned.empty.search') : label('assigned.empty.stockNone')} compact />
            ) : (
              <div className="tw-grid tw-gap-3 md:tw-grid-cols-2 xl:tw-grid-cols-3">
                {stocks.map((s) => <StockCard key={s.stockId} stock={s} search={search} />)}
              </div>
            )
          )}
        </div>
      )}
    </Modal>
  );
};

AssignedStocksModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  data: PropTypes.shape({ byEmployee: PropTypes.array, byStock: PropTypes.array }).isRequired,
  search: PropTypes.string.isRequired,
  setSearch: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired,
  setViewMode: PropTypes.func.isRequired,
  employeeFilter: PropTypes.string.isRequired,
  setEmployeeFilter: PropTypes.func.isRequired,
};

export default AssignedStocksModal;
